import { useState, useContext, useMemo, useEffect, useCallback } from 'react';
import dayjs from 'dayjs';
import { ECreditType, SubscriptionStatus, UserCompanyType, isExternallyBilled } from '@leedpulse-monorepo/enums';
import searchCompanyNumbers, { LeedflowNumberHit } from '../../../../domain/use_cases/subscription/searchCompanyNumbers';
import SubscriptionContext from '../../../contexts/subscription/SubscriptionContext';
import { OrderData } from '../../../components/numbers/modals/order-number/OrderNumberModal';
import CompanyContext from '../../../contexts/company/CompanyContext';
import { isUnlimitedLeedbucks } from '../../../helpers';
import { message } from 'antd';
import AuthenticationContext from '../../../contexts/auth/AuthenticationContext';
import PlansContext from '../../../contexts/plans/PlansContext';
import { formatPhoneNumber, runningCreditsPromo } from '@leedpulse-monorepo/utils';
import { UserCreditsInfo } from '../../users/types/UserDisplay';
import { addNumbersToSubscription } from '../../../../domain/use_cases/stripe/addNumbersToSubscription';
import { cancelNumber } from '../../../../domain/use_cases/stripe/cancelNumber';
import { changePlan } from '../../../../domain/use_cases/stripe/changePlan';
import { cancelPlanChange } from '../../../../domain/use_cases/stripe/cancelPlanChange';
import { assignUserToSubscription } from '../../../../domain/use_cases/subscription/assignUserToSubscription';
import { getCompanySubscriptions } from '../../../../domain/use_cases/subscription/getCompanySubscriptions';
import { getSubscriptionOwnersContacts } from '../../../../domain/use_cases/subscription/getSubscriptionOwnersContacts';
import { ContactsTransferAnswer, useNumberAssignmentSteps } from '../../../hooks/subscription/useNumberAssignmentSteps';
import { IPlan, ISubscription, ICreditMap } from '@leedpulse-monorepo/interfaces';
import { SubscriptionStatusType } from '../../../helpers';

export type ModalType = 'ORDER_NUMBER' | 'ASSIGN_NUMBER' | 'CANCEL_NUMBER' | 'CHANGE_SUBSCRIPTION' | 'CONSUMPTION_HISTORY';

export type CreditStatusFilter = 'ready' | 'warning' | 'exhausted' | 'overage';

// Type not ideal bc json and class  format are not 100% the same
export interface ISubscriptionItem extends Omit<ISubscription, 'sender' | 'status' | 'credits'> {
  id_plan: string;
  plan_start: number;
  plan_end: number;
  sender: string;
  sender_formatted: string;
  usernames: string[];
  credits: Partial<ICreditMap>; // Leedflow: leedbucks only
  credits_info: UserCreditsInfo | null;
  status: SubscriptionStatusType;
}

/**
 * Derive a display status, reading `status` before any date.
 *
 * `status` is the authoritative signal: Stripe cancelling a subscription sets CANCELLED, and a
 * resiliation scheduled for the end of the period sets CANCELLING. `plan_end` only closes the
 * last case, a subscription nobody stopped whose period simply ran out — it carries a month of
 * buffer against a late webhook, so reading it first showed a Stripe-active client as resiliated.
 */
const getSubscriptionStatus = (
  subscription: ISubscription,
  assignedUsersCount: number,
): SubscriptionStatusType => {
  if (subscription.status === SubscriptionStatus.CANCELLED) {
    return SubscriptionStatusType.RESILIÉ;
  }

  if (subscription.status === SubscriptionStatus.PAUSED) {
    return SubscriptionStatusType.PAUSED;
  }

  if (subscription.status === SubscriptionStatus.CANCELLING) {
    return SubscriptionStatusType.RESILIATION_EN_COURS;
  }

  // plan_end is null / 0 / undefined → dayjs wraps it as invalid or epoch
  const end = dayjs(subscription.plan_end);
  if (!end.isValid() || end.isBefore(dayjs())) return SubscriptionStatusType.RESILIÉ;

  return assignedUsersCount > 0 ? SubscriptionStatusType.ACTIF : SubscriptionStatusType.DISPONIBLE;
};

export const useSubscriptionsPageController = () => {
  const numberAssignment = useNumberAssignmentSteps();
  const { authentication, getUserCompanyRole, degradedSubscriptions } = useContext(AuthenticationContext);
  const { activeCompany, users, refreshUsers } = useContext(CompanyContext);
  const { subscriptions: companySubscriptions, refreshSubscriptions } = useContext(SubscriptionContext);
  const { fetchPlanById } = useContext(PlansContext);
  const isCompanyExternallyBilled = isExternallyBilled(activeCompany?.partner_system, activeCompany?.id_stripe);

  // Returns true if a given subscription is degraded AND the company is not externally billed.
  const isSubDegraded = (subscriptionId: string) =>
    !isCompanyExternallyBilled && degradedSubscriptions.some((d) => d.id === subscriptionId);

  const [searchValue, setSearchValue] = useState('');
  const [planFilter, setPlanFilter] = useState<string | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<CreditStatusFilter | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(7);
  const [hits, setHits] = useState<LeedflowNumberHit[]>([]);
  const [totalNumbers, setTotalNumbers] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const isReadOnly = useMemo(() =>
    getUserCompanyRole(activeCompany?.id ?? '') !== UserCompanyType.SUPERVISOR
    , [activeCompany, getUserCompanyRole]);

  // Unified Modal & Loading State
  const [activeModal, setActiveModal] = useState<ModalType | null>(null);
  const [selectedSubscription, setSelectedSubscription] = useState<ISubscriptionItem | null>(null);

  // Cache of plans resolved from subscriptions (plan_id → Plan)
  const [planMap, setPlanMap] = useState<Map<string, IPlan>>(new Map());

  // Resolve plans for company subscriptions + current hits (async), and the target of a scheduled downsell for its label
  useEffect(() => {
    const uniquePlanIds = [...new Set([
      ...companySubscriptions.map(s => s.id_plan),
      ...companySubscriptions.map(s => s.pending_plan_change?.id_plan),
      ...hits.map(h => h.id_plan),
    ].filter(Boolean))] as string[];
    let cancelled = false;

    const resolve = async () => {
      const newEntries: [string, IPlan][] = [];
      for (const planId of uniquePlanIds) {
        const plan = await fetchPlanById(planId);
        if (plan && !cancelled) newEntries.push([planId, plan]);
      }
      if (!cancelled && newEntries.length > 0) {
        setPlanMap(prev => {
          const next = new Map(prev);
          newEntries.forEach(([k, v]) => next.set(k, v));
          return next;
        });
      }
    };
    resolve();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companySubscriptions, hits, fetchPlanById]);

  // The filter offers the plans numbers are on, never the target of a scheduled downsell alone
  const planFilterOptions = useMemo(() => {
    const heldPlanIds = new Set([...companySubscriptions.map(s => s.id_plan), ...hits.map(h => h.id_plan)]);
    return Array.from(planMap.values())
      .filter((plan) => heldPlanIds.has(plan.id))
      .map((plan) => ({ value: plan.id, label: plan.name }));
  }, [planMap, companySubscriptions, hits]);

  const fetchNumbers = useCallback(async () => {
    if (!activeCompany?.id) return;
    setIsLoading(true);
    try {
      // DEV ONLY : Uncomment this to see the numbers listing (not synced in algolia)
      // const fetchedHits = await getCompanySubscriptions(
      //   { company_id: activeCompany.id, subscription_ids: ["subsc-lp-360CMJ7016"] },
      //   authentication!,
      // ) as unknown as LeedflowNumberHit[];
      // const nbHits = fetchedHits.length;

      // DEV ONLY : and comment this
      const { hits: fetchedHits, nbHits } = await searchCompanyNumbers(
        { companyId: activeCompany.id, q: searchValue, page: currentPage - 1, hitsPerPage: pageSize, planId: planFilter, creditStatus: statusFilter },
        authentication,
      );

      setHits(fetchedHits);
      setTotalNumbers(nbHits);
    } catch (error) {
      console.error('Error searching numbers:', error);
      setHits([]);
      setTotalNumbers(0);
    } finally {
      setIsLoading(false);
    }
  }, [activeCompany?.id, authentication, searchValue, currentPage, pageSize, planFilter, statusFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchNumbers();
    }, 400);
    return () => clearTimeout(timer);
  }, [fetchNumbers]);

  // Build display items directly from Algolia hits (1 row per subscription)
  const subscriptions: ISubscriptionItem[] = useMemo(() => {
    const paygEnabledById = new Map(companySubscriptions.map((s) => [s.id, s.settings?.payg_enabled]));
    // Read from Firestore, not from the Algolia hit: a change scheduled seconds ago must show up right away.
    const pendingPlanChangeById = new Map(companySubscriptions.map((s) => [s.id, s.pending_plan_change ?? null]));
    // Same reason, plus the engagement is not indexed at all: the cancellation modal reads it
    const commitmentDateById = new Map(companySubscriptions.map((s) => [s.id, s.commitment_date ?? null]));
    // Same rule as the grant: the running promo replaces the plan's allocation
    // eslint-disable-next-line react/purity
    const iNow = Date.now();
    const promoById = new Map(companySubscriptions.map((s) => [s.id, runningCreditsPromo(s.leedbucks_credits_temp, iNow)]));
    return hits.map(hit => {
      const senders = Array.isArray(hit.sender) ? hit.sender : (hit.sender ? [hit.sender] : []);
      const plan = planMap.get(hit.id_plan ?? '');
      const usernames = hit.usernames ?? [];
      const remaining = {
        leedbucks: hit.credits_leedbucks ?? 0,
      };

      let credits_info: UserCreditsInfo | null = null;
      if (plan) {
        const promo = promoById.get(hit.id);
        credits_info = {
          remaining,
          total: promo ? { ...plan.credits, [ECreditType.leedbucks]: promo.leedbucks } : plan.credits,
          isUnlimitedLeedbucks: isUnlimitedLeedbucks(plan.credits?.[ECreditType.leedbucks] || 0),
          payg_enabled: paygEnabledById.get(hit.id),
        };
      }

      const status = getSubscriptionStatus({ plan_end: hit.plan_end, status: hit.status } as unknown as ISubscription, usernames.length);

      // Normally a subscription has exactly 1 sender. 0 → "N/A", several (dev-forced) → join.
      const sender_formatted = senders.length > 0 ? senders.map(formatPhoneNumber).join(', ') : 'N/A';

      return {
        ...hit,
        id: hit.id,
        api_key: hit.api_key,
        id_plan: hit.id_plan,
        pending_plan_change: pendingPlanChangeById.get(hit.id) ?? null,
        commitment_date: commitmentDateById.get(hit.id) ?? null,
        plan_start: hit.plan_start,
        plan_end: hit.plan_end,
        credits: remaining,
        sender: senders[0] ?? '',
        sender_formatted,
        usernames,
        credits_info,
        status,
      } as unknown as ISubscriptionItem;
    });
  }, [hits, planMap, companySubscriptions]);

  // Helper to open a modal
  const openModal = (type: ModalType, subscription?: ISubscriptionItem) => {
    if (subscription) {
      setSelectedSubscription(subscription);
    }
    setActiveModal(type);
  };

  // Helper to close modals
  const closeModal = () => {
    // A closed modal cancels its running assignment before anything is written
    numberAssignment.cancelAssignment();
    setActiveModal(null);
    setSelectedSubscription(null);
  };

  const [isOrderLoading, setIsOrderLoading] = useState(false);
  const [isCancelLoading, setIsCancelLoading] = useState(false);

  const handleConfirmOrder = async (orderData: OrderData) => {
    if (!activeCompany || !authentication) {
      message.error('Aucune compagnie active ou utilisateur non authentifié');
      return;
    }

    if (!orderData.items || orderData.items.length === 0) {
      message.error('Aucun numéro commandé');
      return;
    }

    setIsOrderLoading(true);
    try {
      const result = await addNumbersToSubscription({
        company_id: activeCompany.id,
        company_api_key: activeCompany.api_key,
        items: orderData.items,
      }, authentication);

      message.success(`${result.created_subscriptions.length} numéro(s) commandé(s) avec succès`);
      closeModal();

      // Refresh data to reflect new subscriptions
      await refreshUsers();
      await fetchNumbers();
    } catch (error) {
      console.error('Order error:', error);
      message.error(error instanceof Error ? error.message : 'Erreur lors de la commande');
    } finally {
      setIsOrderLoading(false);
    }
  };

  const [isAssignLoading, setIsAssignLoading] = useState(false);

  const handleAssignSubscription = async (_numberId: string, subscriptionId: string, userId: string, subscriptionItem?: ISubscriptionItem) => {
    if (!authentication) {
      message.error('Utilisateur non authentifié');
      return;
    }

    // Block assignment on a degraded subscription (paused/cancelled).
    if (isSubDegraded(subscriptionId)) {
      const deg = degradedSubscriptions.find((d) => d.id === subscriptionId);
      message.error(
        deg?.status === 'paused'
          ? "Impossible d'assigner ou réassigner ce numéro : l'abonnement est suspendu."
          : "Impossible d'assigner ou réassigner ce numéro : l'abonnement est résilié.",
      );
      return;
    }

    setIsAssignLoading(true);
    const iAssignment = numberAssignment.startAssignment();
    try {
      // Find the user's current subscription (if any) to unassign from
      let oldSubId: string | undefined;
      if (userId !== 'none' && activeCompany) {
        const targetUser = users.find(u => u.uid === userId);
        if (targetUser) {
          const userSubIds = Object.keys(targetUser.idSubscription || {});

          if (userSubIds.length > 0) {
            const lfSubs = await getCompanySubscriptions(
              { company_id: activeCompany.id, subscription_ids: userSubIds },
              authentication,
            );

            oldSubId = lfSubs.find(sub => sub.id !== subscriptionId)?.id;
          }
        }
      }

      // The contacts on the number go to the new user, leave with their owner or stay on it, as the admin answers
      let transferContacts: ContactsTransferAnswer;
      if (activeCompany) {
        const owners = await getSubscriptionOwnersContacts({ company_id: activeCompany.id, subscription_id: subscriptionId }, authentication);
        const newUser = users.find(u => u.uid === userId);
        const newUserName = userId === 'none'
          ? null
          : `${newUser?.firstname ?? ''} ${newUser?.lastname ?? ''}`.trim() || newUser?.email || 'ce membre';
        const answer = await numberAssignment.askContactsTransfer(iAssignment, owners.filter(o => o.uid !== userId && o.contacts_count > 0), newUserName);
        // The modal was closed meanwhile: nothing is written
        if (answer === null) return;
        transferContacts = answer;
      }
      numberAssignment.setIsAssigning(true);

      // userId = 'none' → unassign, otherwise assign to selected user
      await assignUserToSubscription({
        subscription: subscriptionItem ? subscriptionItem : selectedSubscription,
        user_uid: userId,
        old_subscription_id: oldSubId,
        transfer_contacts: transferContacts,
      }, authentication);

      if (userId === 'none') {
        message.success('Numéro libéré avec succès');
      } else {
        message.success('Numéro assigné avec succès');
      }

      closeModal();
      await refreshUsers();
      await refreshSubscriptions();
      await fetchNumbers();
    } catch (error) {
      console.error('Assign subscription error:', error);
      message.error((error as Error).message || 'Erreur lors de l\'assignation');
    } finally {
      setIsAssignLoading(false);
      numberAssignment.setIsAssigning(false);
    }
  };

  const handleUnassignSubscription = async (subscription: ISubscriptionItem) => {
    // Block unassignment on a degraded subscription (paused/cancelled).
    if (isSubDegraded(subscription.id)) {
      const deg = degradedSubscriptions.find((d) => d.id === subscription.id);
      message.error(
        deg?.status === 'paused'
          ? "Impossible de libérer ce numéro : l'abonnement est suspendu."
          : "Impossible de libérer ce numéro : l'abonnement est résilié.",
      );
      return;
    }
    // Same as picking "Ne pas assigner": the assign modal opens to ask about the contacts on the number and show the progress
    openModal('ASSIGN_NUMBER', subscription);
    await handleAssignSubscription(subscription.sender, subscription.id, 'none', subscription);
  };

  const handleCancelNumber = async (subscription: ISubscriptionItem) => {
    if (!activeCompany || !authentication) {
      message.error('Aucune compagnie active ou utilisateur non authentifié');
      return;
    }

    setIsCancelLoading(true);
    try {
      const { effective_date } = await cancelNumber({
        subscription_id: subscription.id,
        company_id: activeCompany.id,
        company_api_key: activeCompany.api_key,
      }, authentication);

      // `effective_date` is the Stripe period end, already the real date: subtracting a month
      // from it told the client the number died a month before it actually does.
      const formattedDate = dayjs(effective_date).locale('fr').format('DD MMMM YYYY');
      message.success(`Résiliation programmée — le numéro restera actif jusqu'au ${formattedDate}`);
      closeModal();

      // Refresh data to reflect the scheduled cancellation
      await refreshUsers();
      await refreshSubscriptions();
      await fetchNumbers();
    } catch (error) {
      console.error('Cancel number error:', error);
      message.error(error instanceof Error ? error.message : 'Erreur lors de la résiliation');
    } finally {
      setIsCancelLoading(false);
    }
  };

  const [isChangePlanLoading, setIsChangePlanLoading] = useState(false);

  const handleChangeSubscription = async (subscription: ISubscriptionItem, newPlanId: string) => {
    if (!activeCompany || !authentication) {
      message.error('Aucune compagnie active ou utilisateur non authentifié');
      return;
    }

    setIsChangePlanLoading(true);
    try {
      const result = await changePlan({
        subscription_id: subscription.id,
        company_id: activeCompany.id,
        company_api_key: activeCompany.api_key,
        new_plan_id: newPlanId,
      }, authentication);

      if (result.change_type === 'upgrade') { // TODO : @MrWarzo faire un type commun
        message.success('Amélioration du plan effectuée avec succès');
      } else {
        const formattedDate = dayjs(result.effective_date).locale('fr').format('DD MMMM YYYY');
        message.success(`Réduction du plan programmée, elle prendra effet le ${formattedDate}`);
      }
      closeModal();

      // Refresh data to reflect the plan change
      await refreshUsers();
      await refreshSubscriptions();
      await fetchNumbers();
    } catch (error) {
      console.error('Change plan error:', error);
      message.error(error instanceof Error ? error.message : 'Erreur lors du changement de plan');
    } finally {
      setIsChangePlanLoading(false);
    }
  };

  const handleCancelPlanChange = async (subscription: ISubscriptionItem) => {
    if (!activeCompany || !authentication) {
      message.error('Aucune compagnie active ou utilisateur non authentifié');
      return;
    }

    setIsChangePlanLoading(true);
    try {
      await cancelPlanChange({
        subscription_id: subscription.id,
        company_id: activeCompany.id,
        company_api_key: activeCompany.api_key,
      }, authentication);

      message.success('Changement de plan annulé — vous restez sur votre plan actuel');

      // Refresh data to reflect the cancelled change
      await refreshSubscriptions();
      await fetchNumbers();
    } catch (error) {
      console.error('Cancel plan change error:', error);
      message.error(error instanceof Error ? error.message : "Erreur lors de l'annulation du changement de plan");
    } finally {
      setIsChangePlanLoading(false);
    }
  };

  // Build user options for assignment modal — use `uid` (Firebase Auth UID / Firestore doc ID), not `id`
  const userOptions = users.map((user) => ({
    id: user.uid,
    name: `${user.firstname} ${user.lastname}`.trim(),
  }));

  const handleSearchChange = (value: string) => {
    setCurrentPage(1);
    setSearchValue(value);
  };

  const handlePlanFilterChange = (value: string | undefined) => {
    setCurrentPage(1);
    setPlanFilter(value);
  };

  const handleStatusFilterChange = (value: CreditStatusFilter | undefined) => {
    setCurrentPage(1);
    setStatusFilter(value);
  };

  return {
    state: {
      searchValue,
      planFilter,
      statusFilter,
      currentPage,
      pageSize,
      activeModal,
      selectedSubscription,
      numbers: subscriptions,
      totalNumbers,
      isLoading,
      isOrderLoading,
      isCancelLoading,
      isAssignLoading,
      transferQuestion: numberAssignment.transferQuestion,
      isAssigning: numberAssignment.isAssigning,
      isChangePlanLoading,
      userOptions,
      isReadOnly,
      isCompanyExternallyBilled,
      planMap,
      planFilterOptions,
    },
    actions: {
      setSearchValue: handleSearchChange,
      setPlanFilter: handlePlanFilterChange,
      setStatusFilter: handleStatusFilterChange,
      setCurrentPage,
      setPageSize,
      openModal,
      closeModal,
      handleConfirmOrder,
      handleAssignSubscription,
      answerContactsTransfer: numberAssignment.answerContactsTransfer,
      handleUnassignSubscription,
      handleCancelNumber,
      handleChangeSubscription,
      handleCancelPlanChange,
    },
  };
};
